import React from 'react'
import { Box } from '@mui/material'
import BoltIcon from '@mui/icons-material/Bolt'
import GroupsIcon from '@mui/icons-material/Groups'
import PersonIcon from '@mui/icons-material/Person'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { __t } from '../../../../i18n/t'
import { PUSH_MAX, NAME_TOKEN, SendMode, addNameToPush, addNameToTitle, autoBody, minSendAt, recipients, sendAfterEnd, suggestPush } from '../model'
import { useWiz } from '../state/WizardContext'
import { FieldError, Hint, Rich } from '../ui/basics'
import { LabeledInput, OutlineButton, RadioCard, RadioRow, TextButton } from '../ui/controls'
import { C, CALLOUT } from '../ui/tokens'

// Estilo fora do JSX: o plugin de i18n embrulha literal de texto dentro de `&&`/ternário (ver ui/tokens.ts).
const choiceSx = { display: 'flex', flexWrap: 'wrap', gap: 1.25, '& > label': { flex: '1 1 240px', maxWidth: { sm: 420 } } } as const
const formSx = { display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 680 } as const
const whoBoxSx = { display: 'flex', flexDirection: 'column', gap: 1.25, p: '12px 14px', borderRadius: '10px', bgcolor: C.surface, border: `1px solid ${C.line}` } as const
const whoRowSx = { display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' } as const
const whoTextSx = { display: 'flex', gap: 1.25, alignItems: 'center', fontSize: 14, color: C.ink, minWidth: 0, overflowWrap: 'anywhere' } as const
const suggestSx = { height: 'auto', minHeight: 40, py: 0.75, px: 1.5, fontSize: 13.5, whiteSpace: 'normal', textAlign: 'left' } as const
const fieldSx = { display: 'flex', flexDirection: 'column', gap: 0.75 } as const
const labelSx = { fontSize: 14.5, fontWeight: 500, color: C.ink } as const
const optionalSx = { fontWeight: 400, color: C.muted } as const
const counterRowSx = { display: 'flex', justifyContent: 'space-between', gap: 1.5, maxWidth: 640, fontSize: 12.5, color: C.muted } as const
const counterSx = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const
const sendSx = { display: 'flex', flexDirection: 'column', gap: 1.25 } as const
const afterEndSx = { display: 'flex', gap: 0.75, alignItems: 'center', fontSize: 13, color: CALLOUT.warnText } as const
const cardTightSx = { p: '12px 14px' } as const
const iconSuggestSx = { color: `${C.violet} !important` } as const

/** Etapa 4 (só Especial): avisar os clientes pelo app. Diz com franqueza para quem a notificação vai. */
export const StepNotice: React.FC = () => {
  const { c, d, set, fe, act } = useWiz()
  const recip = recipients(d, c)
  const titleHasName = d.title.includes(NAME_TOKEN)
  const pushHasName = d.pushText.includes(NAME_TOKEN)
  const later = d.sendMode === SendMode.later
  const over = d.pushText.length > PUSH_MAX
  const counter = d.pushText.length + '/' + PUSH_MAX
  const nameLabel = (has: boolean) => (has ? __t('Nome do cliente adicionado') : __t('Adicionar nome do cliente'))
  const labelOf = (text: string) => <Box component="span" sx={labelSx}>{text}</Box>

  return (
    <>
      <Box role="radiogroup" aria-labelledby="step-q" sx={choiceSx}>
        <RadioCard name="notify" checked={!d.notify} onChange={act.turnOffNotify} title={__t('Não avisar')} />
        <RadioCard name="notify" checked={d.notify} onChange={act.turnOnNotify} title={__t('Mandar notificação push')} />
      </Box>

      {d.notify ? (
        <Box sx={formSx}>
          <Box sx={whoBoxSx}>
            <Box sx={whoRowSx}>
              <Box sx={whoTextSx}>
                <GroupsIcon aria-hidden sx={{ color: C.violet, flexShrink: 0 }} />
                <span><Rich text={__t('Quem recebe: **%{quem}**', { quem: recip.text })} /></span>
              </Box>
              <OutlineButton onClick={() => set(x => ({ ...x, ...suggestPush(x, c) }))} icon={<BoltIcon sx={iconSuggestSx} />} sx={suggestSx}>
                {__t('Preencher com os dados da promoção')}
              </OutlineButton>
            </Box>
            {recip.note ? <Hint>{recip.note}</Hint> : null}
          </Box>

          <Box sx={fieldSx}>
            <LabeledInput
              id="f-title"
              label={labelOf(__t('Título'))}
              width={480}
              value={d.title}
              placeholder={__t('Ex.: R$ 0,20 de desconto no Etanol')}
              onChange={e => set(x => ({ ...x, title: e.target.value }))}
              invalid={!!fe['f-title']}
              describedBy={fe['f-title'] ? 'n-title-err' : undefined}
              right={<TextButton onClick={() => set(addNameToTitle)} disabled={titleHasName} icon={<PersonIcon />}>{nameLabel(titleHasName)}</TextButton>}
            />
            {fe['f-title'] ? <FieldError id="n-title-err">{fe['f-title']}</FieldError> : null}
          </Box>

          <Box sx={fieldSx}>
            <LabeledInput
              id="f-push"
              label={labelOf(__t('Texto da notificação'))}
              width={640}
              multiline
              minRows={3}
              value={d.pushText}
              placeholder={__t('Ex.: É só neste fim de semana no Posto Centro. Abasteça pelo app.')}
              onChange={e => set(x => ({ ...x, pushText: e.target.value }))}
              invalid={!!fe['f-push'] || over}
              describedBy={['n-push-count', fe['f-push'] ? 'n-push-err' : ''].filter(Boolean).join(' ')}
              right={<TextButton onClick={() => set(addNameToPush)} disabled={pushHasName} icon={<PersonIcon />}>{nameLabel(pushHasName)}</TextButton>}
            />
            <Box sx={counterRowSx}>
              <span>{pushHasName || titleHasName ? __t('%{token} vira o primeiro nome do cliente.', { token: NAME_TOKEN }) : null}</span>
              <Box id="n-push-count" component="span" sx={[counterSx, { color: over ? C.red : C.muted }]}>{counter}</Box>
            </Box>
            {fe['f-push'] ? <FieldError id="n-push-err">{fe['f-push']}</FieldError> : null}
          </Box>

          <Box sx={fieldSx}>
            <LabeledInput
              id="n-body"
              label={<Box component="span" sx={labelSx}>{__t('Mensagem completa, ao tocar na notificação')} <Box component="span" sx={optionalSx}>{__t('(opcional)')}</Box></Box>}
              width={640}
              multiline
              minRows={3}
              value={d.body}
              placeholder={autoBody(d, c)}
              onChange={e => set(x => ({ ...x, body: e.target.value }))}
              describedBy="n-body-help"
            />
            <Hint id="n-body-help" sx={{ fontSize: 12.5, maxWidth: 640 }}>{__t('Em branco, vai o texto de exemplo.')}</Hint>
          </Box>

          <Box sx={sendSx}>
            <Box id="h-envio" component="span" sx={labelSx}>{__t('Quando enviar?')}</Box>
            <RadioRow labelledBy="h-envio">
              <RadioCard name="send" checked={!later} onChange={() => set(x => ({ ...x, sendMode: SendMode.now }))} title={__t('Logo depois de salvar')} desc={__t('Chega em até 10 minutos.')} sx={cardTightSx} />
              <RadioCard name="send" checked={later} onChange={() => set(x => ({ ...x, sendMode: SendMode.later }))} title={__t('Agendar')} sx={cardTightSx} />
            </RadioRow>
            {later ? <ScheduleFields /> : null}
          </Box>
        </Box>
      ) : null}
    </>
  )
}

/** Dia e horário do envio agendado, com o aviso de quem agenda para depois do fim da promoção. */
const ScheduleFields: React.FC = () => {
  const { c, d, set, fe } = useWiz()
  return (
    <Box sx={fieldSx}>
      <LabeledInput
        id="f-sendat"
        label={__t('Dia e horário (de Brasília)')}
        type="datetime-local"
        width={240}
        min={minSendAt(c.now)}
        value={d.sendAt}
        onChange={e => set(x => ({ ...x, sendAt: e.target.value }))}
        invalid={!!fe['f-sendat']}
        describedBy={fe['f-sendat'] ? 'n-send-err' : undefined}
      />
      {fe['f-sendat'] ? <FieldError id="n-send-err">{fe['f-sendat']}</FieldError> : null}
      <Box role="status">
        {sendAfterEnd(d) ? (
          <Box sx={afterEndSx}>
            <WarningAmberIcon aria-hidden sx={{ fontSize: 16, color: CALLOUT.warnIcon, flexShrink: 0 }} />
            {__t('Nesse horário a promoção já terá terminado.')}
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}
